import React from "react";

interface HolmesRaheScoreDisplayProps {
  scores: Record<string, any>;
  interpretations: Record<string, any>;
}

const HolmesRaheScoreDisplay: React.FC<HolmesRaheScoreDisplayProps> = ({ 
  scores, 
  interpretations 
}) => {
  // Helper function to determine risk level based on UCV score
  const getRiskLevel = (score: number): string => {
    if (score < 150) return "Bajo";
    if (score < 300) return "Moderado";
    return "Alto";
  };

  // Helper function to get risk description
  const getRiskDescription = (score: number): string => {
    if (score < 150) {
      return "Riesgo bajo de desarrollar enfermedades. Probabilidad de aproximadamente 30% de desarrollar una enfermedad relacionada con el estrés en los próximos dos años.";
    }
    if (score < 300) {
      return "Riesgo moderado de desarrollar enfermedades. Probabilidad de aproximadamente 50% de desarrollar una enfermedad relacionada con el estrés en los próximos dos años.";
    }
    return "Riesgo alto de desarrollar enfermedades. Probabilidad de aproximadamente 80% de desarrollar una enfermedad relacionada con el estrés en los próximos dos años.";
  };

  // Helper function to get color based on UCV score
  const getScoreColor = (score: number): string => {
    if (score < 150) return "text-green-500";
    if (score < 300) return "text-yellow-500";
    return "text-red-500";
  };

  // Helper function to get background color based on UCV score
  const getBarColor = (score: number): string => {
    if (score < 150) return "bg-green-500";
    if (score < 300) return "bg-yellow-500";
    return "bg-red-500";
  };

  // Helper function to get bar width percentage
  const getBarWidth = (score: number): string => {
    // Scale the score relative to maximum of 450 (approximate max in Holmes-Rahe scale)
    const percentage = Math.min(100, (score / 450) * 100);
    return `${Math.max(5, percentage)}%`;
  };
  
  // Debug log
  console.log("HOLMES_RAHE scores:", scores);
  console.log("HOLMES_RAHE interpretations:", interpretations);

  // Get the total UCV score
  const totalUCVScore = scores.total_ucv?.raw_score || 0;
  const riskLevel = getRiskLevel(totalUCVScore);
  const riskDescription = getRiskDescription(totalUCVScore);
  const textColor = getScoreColor(totalUCVScore);
  const barColor = getBarColor(totalUCVScore);
  const barWidth = getBarWidth(totalUCVScore);

  return (
    <div className="space-y-8">
      <div className="mb-6">
        <h5 className="text-lg font-bold text-navy-700 dark:text-white mb-2">
          Escala de Reajuste Social de Holmes y Rahe
        </h5>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Evalúa el nivel de estrés basado en eventos vitales experimentados en los últimos 12 meses
        </p>

        <div className="bg-white dark:bg-navy-800 rounded-xl p-5 shadow-md mb-6">
          <div className="flex flex-wrap justify-between items-center mb-2">
            <span className="text-md font-medium text-gray-700 dark:text-gray-300">
              {scores.total_ucv?.name || "Total de Unidades de Cambio Vital (UCV)"}
            </span>
            <div className="flex items-center">
              <span className="text-2xl font-bold text-gray-800 dark:text-white mr-3">
                {totalUCVScore}
              </span>
              <span className={`text-md font-semibold ${textColor}`}>
                Riesgo {riskLevel}
              </span>
            </div>
          </div>
          
          <div className="w-full bg-gray-200 dark:bg-navy-700 rounded-full h-3 mb-3">
            <div 
              className={`h-3 rounded-full ${barColor}`} 
              style={{ width: barWidth }}
            ></div>
          </div>
          
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
            {scores.total_ucv?.description || "Suma total de las unidades de cambio vital de los eventos seleccionados"}
          </p>
          
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className={`text-sm font-medium ${textColor}`}>
              {riskDescription}
            </p>
          </div>
        </div>
      </div>

      {/* Legend and Interpretation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-navy-800 rounded-xl p-5 shadow-md">
          <h5 className="text-md font-bold text-navy-700 dark:text-white mb-3">
            Leyenda
          </h5>
          <div className="space-y-3">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
              <span className="text-sm">
                <span className="font-medium">Riesgo Bajo:</span> &lt; 150 UCV
              </span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
              <span className="text-sm">
                <span className="font-medium">Riesgo Moderado:</span> 150-299 UCV
              </span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
              <span className="text-sm">
                <span className="font-medium">Riesgo Alto:</span> ≥ 300 UCV
              </span>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-navy-800 rounded-xl p-5 shadow-md">
          <h5 className="text-md font-bold text-navy-700 dark:text-white mb-3">
            Acerca de la Escala
          </h5>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            La escala de Holmes y Rahe asigna "Unidades de Cambio Vital" (UCV) a diferentes eventos vitales.
            La suma de estas unidades durante los últimos 12 meses indica el nivel de estrés y el riesgo potencial
            de desarrollar problemas de salud relacionados con el estrés.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Este instrumento es utilizado para estimar la vulnerabilidad al estrés y como predictor de posibles
            enfermedades relacionadas.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HolmesRaheScoreDisplay; 